import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Check, ArrowLeft, Clock } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/Motion";
import BookingDialog from "@/components/BookingDialog";
import Seo from "@/components/Seo";
import api from "@/lib/api";

export default function ServizioDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get(`/services/${slug}`).then((r) => setS(r.data)).catch(() => nav("/servizi"));
  }, [slug, nav]);

  if (!s) return <div className="min-h-screen flex items-center justify-center text-gold font-display">Caricamento…</div>;
  const isCourse = s.category === "Formazione Lux Academy";

  return (
    <div className="pt-24 pb-28 min-h-screen">
      <Seo title={s.title} description={s.subtitle ? `${s.subtitle} ${s.description || ""}` : s.description} image={s.image} type="product"
        jsonLd={{ "@context": "https://schema.org", "@type": "Service", name: s.title, description: s.description, image: s.image, serviceType: s.category,
          provider: { "@type": "Organization", name: "LUX AETERNA" }, areaServed: "IT",
          offers: s.price && /\d/.test(s.price) ? { "@type": "Offer", price: s.price.replace(/[^\d,.]/g, "").replace(",", "."), priceCurrency: "EUR", availability: "https://schema.org/InStock" } : undefined }} />
      <div className="relative h-[50vh] min-h-[360px] overflow-hidden constellation">
        <img src={s.image} alt={s.title} className="w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F7EFE0] via-[#F7EFE0]/60 to-transparent" />
        <div className="absolute inset-0 max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-12">
          <Link to="/servizi" className="inline-flex items-center gap-2 text-[#2A1F14]/50 hover:text-gold text-sm mb-6 w-fit"><ArrowLeft size={16} /> Servizi</Link>
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">{s.category}</p>
          <h1 className="font-display text-4xl sm:text-6xl text-[#2A1F14] leading-tight">{s.title}</h1>
          {s.subtitle && <p className="font-display italic text-xl text-[#2A1F14]/60 mt-3">{s.subtitle}</p>}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-16 mt-16">
        <div className="lg:col-span-2 space-y-14">
          <Reveal>
            <h2 className="font-display text-2xl text-gold mb-5">Descrizione</h2>
            <p className="text-[#2A1F14]/65 text-lg leading-relaxed">{s.description}</p>
          </Reveal>

          {s.benefits?.length > 0 && (
            <Reveal>
              <h2 className="font-display text-2xl text-gold mb-6">Benefici</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {s.benefits.map((b) => (
                  <div key={b} className="flex items-start gap-3 border border-[#2A1F14]/10 rounded-xl p-5">
                    <Check size={18} className="text-gold mt-0.5 shrink-0" /><span className="text-[#2A1F14]/75 text-sm">{b}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {s.faq?.length > 0 && (
            <Reveal>
              <h2 className="font-display text-2xl text-gold mb-6">Domande frequenti</h2>
              <Accordion type="single" collapsible className="border-t border-[#2A1F14]/10">
                {s.faq.map((f, i) => (
                  <AccordionItem key={i} value={`f${i}`} className="border-[#2A1F14]/10">
                    <AccordionTrigger data-testid={`faq-${i}`} className="text-[#2A1F14] hover:text-gold text-left">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[#2A1F14]/55 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="glass rounded-2xl p-8 sticky top-24">
            <div className="flex items-center justify-between mb-6">
              <span className="inline-flex items-center gap-1.5 text-[#2A1F14]/60 text-sm"><Clock size={16} className="text-gold" />{s.duration}</span>
              <span className="font-display text-2xl text-gold">{s.price}</span>
            </div>
            <h3 className="font-display text-2xl text-[#2A1F14] mb-3">{isCourse ? "Iscriviti" : "Prenota"}</h3>
            <p className="text-[#2A1F14]/50 text-sm mb-8 leading-relaxed">Invia la tua richiesta per "{s.title}". Ti ricontatteremo per confermare data e modalità.</p>
            <BookingDialog title={`${isCourse ? "Iscrizione" : "Prenota"} — ${s.title}`}
              endpoint={isCourse ? "enrollments" : "bookings"}
              presets={isCourse ? { course: s.title } : { type: "servizio", service: s.title }}
              trigger={<button data-testid="service-book" className="w-full py-4 rounded-full bg-gold text-black text-sm font-medium hover:bg-[#D4A056] transition-colors">{isCourse ? "Iscriviti ora" : "Prenota ora"}</button>} />
          </div>
        </div>
      </div>
    </div>
  );
}
